import React, { useState, useEffect } from 'react';
import { dynastyTokens } from '../../../services/colorService';
import PlayerLink from '../../../components/PlayerLink';
import { leaguesAPI } from '../../../services/apiService';

const colors = dynastyTokens.colors;

// DD-RO-GAPS-0727: the three-way basis toggle. Choice does NOT persist — resets to 'current' each mount.
const BASES = [
  { key: 'current', label: 'Current' },
  { key: 'projected', label: 'Projected' },
  { key: 'projected_selection', label: 'With selection' },
];

/* ── Utility functions ── */
const statusColor = (s) => s === 'dominant' ? colors.primary : s === 'stable' ? colors.success : s === 'improvable' ? colors.warning : colors.error;
const rankColor = (r) => r <= 3 ? colors.success : r <= 7 ? colors.warning : colors.error;
const ordSuffix = (n) => { if (!n) return '—'; const s = ['th','st','nd','rd']; const v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };
const capFlexColor = (f) => f === 'flexible' ? colors.success : f === 'moderate' ? colors.warning : colors.error;

const cellBg = (rank) => {
  if (rank <= 2) return `${colors.success}20`;
  if (rank <= 4) return `${colors.success}0c`;
  if (rank <= 7) return `${colors.warning}10`;
  if (rank <= 9) return `${colors.warning}08`;
  return `${colors.error}14`;
};

const recTypeColor = (t) => {
  if (t === 'best_overall') return colors.primary;
  if (t === 'safest') return colors.success;
  if (t === 'highest_upside') return colors.dataPurple;
  if (t === 'best_dynasty') return colors.info;
  if (t === 'best_category_fix') return colors.warning;
  return colors.info;
};

const STATUS_PRIORITY = { priority: 0, improvable: 1, stable: 2, dominant: 3 };

/* ── Section header ── */
const SectionLabel = ({ label, right }) => (
  <div style={{ fontSize: 10, fontWeight: 700, color: colors.primary, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ width: 3, height: 12, background: colors.primary, borderRadius: 2 }} />
    {label}
    {right && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 500, color: colors.neutral[500], textTransform: 'none', letterSpacing: 0 }}>{right}</span>}
  </div>
);

/* ── Main component ── */
const OptimizerGapAnalysis = ({ optimizerData, league, userTeam, teams, onCategorySelect,
                                leagueId, selectedCandidate, subjectPlayer }) => {
  // DD-RO-GAPS-0727: the heat-map ranks come from the authoritative standings endpoint, on the selected
  // basis. Default 'current' = the Rotisserie Standings page exactly (read, not recomputed). 'projected' =
  // stand-pat finish; 'projected_selection' = finish with the highlighted FA. Does NOT persist across mounts.
  const [basis, setBasis] = useState('current');
  const [standings, setStandings] = useState(null);
  const teamId = userTeam?.team_id;
  const selectionId = selectedCandidate?.ids?.mlb ?? null;
  const subjectId = subjectPlayer?.ids?.mlb ?? null;   // DD-RO-GAPS-CLUSTER-0728 4.2: subject-out (the drop)
  const basisLabel = basis === 'current' ? 'Current' : basis === 'projected' ? 'Projected' : 'With selection';
  useEffect(() => {
    if (!leagueId || !teamId) return;
    let alive = true;
    const useBasis = (basis === 'projected_selection' && selectionId == null) ? 'projected' : basis;
    leaguesAPI.getTeamCategoryStandings(leagueId, teamId, useBasis,
                                        useBasis === 'projected_selection' ? selectionId : null,
                                        useBasis === 'projected_selection' ? subjectId : null)
      .then(r => { if (alive) setStandings(r); })
      .catch(() => { if (alive) setStandings(null); });
    return () => { alive = false; };
  }, [leagueId, teamId, basis, selectionId, subjectId]);
  // Category → { rank, value, points } from the fetched standings (basis-driven); overlays the embedded gaps.
  const rankByCat = {};
  if (standings?.categories) {
    for (const [cat, cd] of Object.entries(standings.categories)) rankByCat[cat] = cd;
  }
  const catRank = (gap) => rankByCat[gap.category]?.rank ?? gap.current_rank;
  const catPts = (gap) => (rankByCat[gap.category] ? rankByCat[gap.category].points : (gap.standings_points ?? 0));

  if (!optimizerData) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: colors.neutral[400] }}>
        <div style={{ width: 20, height: 20, marginRight: 8, border: `2px solid ${colors.neutral[600]}`, borderTopColor: colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        Loading gap analysis...
      </div>
    );
  }

  const gaps = optimizerData.category_gaps || [];
  const tradeEngine = optimizerData.trade_engine;
  const recommendations = tradeEngine?.recommendations || [];
  const teamNeeds = tradeEngine?.team_needs?.[userTeam?.team_id];
  const totalTeams = teams?.length || 10;

  // DD-RO-FINISH-0727 item 3C: "points available from your current lineup, no move required" — format-aware.
  // In a no-usable-bench league (HCML) this is ~0: render it minimally and honestly (0/None handled), never
  // a dead headline. When > 0, name who to start (an off-IL activation or a bench swap).
  const freeLineup = optimizerData.free_lineup || null;
  const freePoints = freeLineup?.points;
  const newlyStarting = freeLineup?.newly_starting || [];

  // Split gaps by hitting / pitching
  const metaCats = optimizerData.meta?.categories;
  const hittingCatSet = new Set(metaCats?.hitting || league?.league_settings?.scoring_categories?.hitting || []);
  const pitchingCatSet = new Set(metaCats?.pitching || league?.league_settings?.scoring_categories?.pitching || []);
  const hittingGaps = gaps.filter(g => hittingCatSet.has(g.category));
  const pitchingGaps = gaps.filter(g => pitchingCatSet.has(g.category));

  const hittingPts = hittingGaps.reduce((s, g) => s + (g.standings_points || 0), 0);
  const pitchingPts = pitchingGaps.reduce((s, g) => s + (g.standings_points || 0), 0);

  // Actionable gaps for action priority
  const actionableGaps = [...gaps]
    .filter(g => g.best_move_summary)
    .sort((a, b) => (STATUS_PRIORITY[a.status] ?? 4) - (STATUS_PRIORITY[b.status] ?? 4));

  // Gainable points from priority + improvable
  const gainableFiltered = gaps.filter(g => g.status === 'priority' || g.status === 'improvable');
  const gainablePts = gainableFiltered.reduce((s, g) => s + (g.max_gainable_points || 0), 0);
  const gainableCount = gainableFiltered.length;

  // Top 3 recommendations
  const topRecs = recommendations.slice(0, 3);

  return (
    <div style={{ overflowY: 'auto', padding: '0 0 24px', flex: 1, minHeight: 0 }}>

      {/* ── Section 1: Recommended Moves ── */}
      {topRecs.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionLabel label="RECOMMENDED MOVES" />
          <div style={{ display: 'flex', gap: 8 }}>
            {topRecs.map((rec, i) => {
              const tc = recTypeColor(rec.recommendation_type);
              return (
                <div
                  key={i}
                  style={{
                    flex: 1, background: colors.neutral[800],
                    border: `1px solid ${colors.neutral[700]}`,
                    borderTop: `2px solid ${tc}`,
                    borderRadius: 4, padding: '10px 12px',
                    transition: 'border-color 0.15s', cursor: 'default',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = tc; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = colors.neutral[700]; e.currentTarget.style.borderTopColor = tc; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                      background: `${tc}20`, color: tc,
                    }}>
                      #{rec.rank}
                    </span>
                    <span style={{ fontSize: 9, fontWeight: 700, color: tc, letterSpacing: 1, textTransform: 'uppercase' }}>
                      {(rec.recommendation_type || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.white, marginBottom: 4 }}>
                    {rec.headline}
                  </div>
                  <div style={{ fontSize: 10, color: colors.neutral[500], lineHeight: 1.3 }}>
                    {rec.reasoning}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── DD-RO-GAPS-0727: basis toggle — Current (authoritative) / Projected / With selection ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        {BASES.map(b => {
          const disabled = b.key === 'projected_selection' && selectionId == null;
          const active = basis === b.key && !disabled;
          return (
            <button key={b.key} disabled={disabled}
              onClick={() => setBasis(b.key)}
              title={disabled ? 'Select an FA candidate to preview its projected standings' : ''}
              style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.4, padding: '3px 9px', borderRadius: 3,
                border: `1px solid ${active ? colors.primary : colors.neutral[700]}`,
                color: disabled ? colors.neutral[600] : (active ? colors.primary : colors.neutral[400]),
                background: active ? `${colors.primary}18` : 'transparent',
                cursor: disabled ? 'not-allowed' : 'pointer', textTransform: 'uppercase',
              }}>{b.label}{b.key === 'projected_selection' && selectedCandidate
                ? `: ${selectedCandidate.info?.full_name?.split(' ').slice(-1)[0] || ''}` : ''}</button>
          );
        })}
        <span style={{ fontSize: 9, color: colors.neutral[600], marginLeft: 'auto' }}>
          {basis === 'current' ? 'authoritative standings' : 'accrued + rest-of-season projection'}
        </span>
      </div>

      {/* ── DD-RO-FINISH-0727 item 3C: free lineup points (format-aware; minimal when ~0) ── */}
      {freeLineup != null && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '6px 10px',
          background: colors.neutral[850], borderRadius: 4,
          border: `1px solid ${(freePoints || 0) > 0 ? colors.success : colors.neutral[800]}`,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: colors.neutral[400],
                         textTransform: 'uppercase' }}>Free points from your lineup</span>
          <span style={{ fontSize: 15, fontWeight: 700,
                         color: (freePoints || 0) > 0 ? colors.success : colors.neutral[500] }}>
            {freePoints == null ? '—' : `+${freePoints}`}
          </span>
          <span style={{ fontSize: 10, color: colors.neutral[500] }}>
            {(freePoints || 0) > 0 && newlyStarting.length > 0
              ? `— start ${newlyStarting.map(p => p.player_name).filter(Boolean).join(', ')}`
              : 'your current lineup is optimal — no move needed'}
          </span>
        </div>
      )}

      {/* ── Section 2: Heat Map Tiles (the Category Gaps table) ── */}
      {/* DD-RO-GAPS-CLUSTER-0728 Root A: label the table with its LIVE toggle basis, so it never reads as a
          silent contradiction of the projected Action Priority list below. */}
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: colors.neutral[400],
                    textTransform: 'uppercase', marginBottom: 6 }} data-testid="gaps-table-basis">
        Category Gaps <span style={{ color: colors.primary }}>({basisLabel})</span>
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        {/* Hitting */}
        <div style={{ flex: 1 }}>
          <SectionLabel label="HITTING" right={`${hittingPts} pts`} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4 }}>
            {hittingGaps.map(gap => (
              <div key={gap.category} style={{
                background: cellBg(catRank(gap)), borderRadius: 4, padding: '6px 4px',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
              }}>
                <span style={{ fontSize: 9, color: colors.neutral[400], textTransform: 'uppercase', letterSpacing: 0.5 }}>{gap.category}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: rankColor(catRank(gap)) }}>{catRank(gap)}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ fontSize: 9, color: colors.neutral[500] }}>{catPts(gap)}pts</span>
                  <div style={{ width: 4, height: 4, borderRadius: '50%', background: statusColor(gap.status) }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Pitching */}
        <div style={{ flex: 1 }}>
          <SectionLabel label="PITCHING" right={`${pitchingPts} pts`} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4 }}>
            {pitchingGaps.map(gap => (
              <div key={gap.category} style={{
                background: cellBg(catRank(gap)), borderRadius: 4, padding: '6px 4px',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
              }}>
                <span style={{ fontSize: 9, color: colors.neutral[400], textTransform: 'uppercase', letterSpacing: 0.5 }}>{gap.category}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: rankColor(catRank(gap)) }}>{catRank(gap)}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ fontSize: 9, color: colors.neutral[500] }}>{catPts(gap)}pts</span>
                  <div style={{ width: 4, height: 4, borderRadius: '50%', background: statusColor(gap.status) }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Section 3: Your Leverage ── */}
      {teamNeeds && (
        <div style={{ marginBottom: 16 }}>
          <SectionLabel label="YOUR LEVERAGE" />
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Card 1: Category Surplus */}
            <div style={{ flex: 1, background: colors.neutral[800], border: `1px solid ${colors.neutral[700]}`, borderRadius: 4, padding: '10px 12px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: colors.neutral[400], letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>CATEGORY SURPLUS</div>
              {(teamNeeds.category_surplus || []).length > 0
                ? teamNeeds.category_surplus.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: colors.success, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: colors.neutral[300], flex: 1 }}>{s.category}</span>
                    <span style={{ fontSize: 10, color: colors.neutral[500] }}>{ordSuffix(s.rank)}</span>
                    <span style={{ fontSize: 10, color: colors.success }}>+{s.cushion_below}</span>
                  </div>
                ))
                : <div style={{ fontSize: 10, color: colors.neutral[600] }}>None</div>
              }
            </div>

            {/* Card 2: Position Intel */}
            <div style={{ flex: 1, background: colors.neutral[800], border: `1px solid ${colors.neutral[700]}`, borderRadius: 4, padding: '10px 12px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: colors.neutral[400], letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>POSITION SURPLUS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 8 }}>
                {(teamNeeds.positional_surplus || []).length > 0
                  ? teamNeeds.positional_surplus.map((p, i) => (
                    <span key={i} style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: `${colors.success}20`, color: colors.success }}>{p}</span>
                  ))
                  : <span style={{ fontSize: 10, color: colors.neutral[600] }}>None</span>
                }
              </div>
              <div style={{ fontSize: 9, fontWeight: 700, color: colors.neutral[400], letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>NEEDS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {(teamNeeds.positional_needs || []).length > 0
                  ? teamNeeds.positional_needs.map((p, i) => (
                    <span key={i} style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: `${colors.error}20`, color: colors.error }}>{p}</span>
                  ))
                  : <span style={{ fontSize: 10, color: colors.neutral[600] }}>None</span>
                }
              </div>
            </div>

            {/* Card 3: Dead Weight */}
            <div style={{ flex: 1, background: colors.neutral[800], border: `1px solid ${colors.neutral[700]}`, borderRadius: 4, padding: '10px 12px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: colors.neutral[400], letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>DEAD WEIGHT TO SHED</div>
              {(teamNeeds.contract_problems || []).length > 0
                ? teamNeeds.contract_problems.map((cp, i) => (
                  <div key={i} style={{ padding: '2px 0' }}>
                    <PlayerLink mlbId={cp.player_id} name={cp.player_name} style={{ fontSize: 11, fontWeight: 600, color: colors.error }} />
                    <span style={{ fontSize: 10, color: colors.neutral[500], marginLeft: 6 }}>${cp.contract_salary} · {cp.liability}</span>
                  </div>
                ))
                : <div style={{ fontSize: 10, color: colors.neutral[600] }}>None</div>
              }
            </div>

            {/* Card 4: Trade Capital */}
            <div style={{ flex: 1, background: colors.neutral[800], border: `1px solid ${colors.neutral[700]}`, borderRadius: 4, padding: '10px 12px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: colors.neutral[400], letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>TRADE CAPITAL</div>
              {teamNeeds.draft_capital && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span style={{ fontSize: 10, color: colors.neutral[500] }}>Draft Picks</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: colors.white }}>{teamNeeds.draft_capital.picks_count}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span style={{ fontSize: 10, color: colors.neutral[500] }}>Premium (R1-3)</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: colors.primary }}>{teamNeeds.draft_capital.high_value_picks}</span>
                  </div>
                </>
              )}
              {teamNeeds.cap_situation && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span style={{ fontSize: 10, color: colors.neutral[500] }}>Cap Room</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: colors.white }}>${teamNeeds.cap_situation.cap_remaining}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span style={{ fontSize: 10, color: colors.neutral[500] }}>Flexibility</span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                      background: `${capFlexColor(teamNeeds.cap_situation.flexibility)}20`,
                      color: capFlexColor(teamNeeds.cap_situation.flexibility),
                    }}>
                      {teamNeeds.cap_situation.flexibility}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Section 4: Action Priority + Contract Liabilities ── */}
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Left: Action Priority — DD-RO-GAPS-CLUSTER-0728 Root A / consistency #5: this list is ALWAYS
            PROJECTED (it targets the projected finish; recommending against current/July standings recreates
            the killed July bug). Labeled "(Projected)" so it never silently contradicts the toggle-driven
            Category Gaps table above. It does NOT follow the toggle — by design. */}
        <div style={{ flex: 2 }}>
          <SectionLabel label="ACTION PRIORITY (PROJECTED)" right={`${actionableGaps.length} actionable categories`} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {actionableGaps.map(gap => {
              const sc = statusColor(gap.status);
              const rc = rankColor(gap.current_rank);
              // DD-RO-BUGS2-0724 Bug 4: the tile hover-lit its border (a clickable affordance) but
              // had no onClick and no handler prop existed — a §4.5.21 dead control. Wire it to the
              // drill-down it implies: open the Sequences view filtered to this category. Only
              // interactive when a handler is provided (otherwise it's an honest static tile).
              const clickable = !!onCategorySelect;
              const drill = () => { if (clickable) onCategorySelect(gap.category); };
              return (
                <div
                  key={gap.category}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  data-testid="action-priority-tile"
                  onClick={drill}
                  onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); drill(); } } : undefined}
                  style={{
                    background: colors.neutral[800], border: `1px solid ${colors.neutral[700]}`,
                    borderLeft: `3px solid ${sc}`, borderRadius: 4, padding: '8px 10px',
                    display: 'flex', alignItems: 'center', gap: 8, transition: 'border-color 0.15s',
                    cursor: clickable ? 'pointer' : 'default',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderLeftColor = colors.white; }}
                  onMouseLeave={e => { e.currentTarget.style.borderLeftColor = sc; }}
                >
                  <div style={{
                    width: 34, height: 26, borderRadius: 4, flexShrink: 0,
                    background: `${rc}20`, color: rc,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                  }}>
                    {ordSuffix(gap.current_rank)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: colors.white }}>{gap.category}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                        background: `${sc}20`, color: sc, textTransform: 'uppercase',
                      }}>
                        {gap.status}
                      </span>
                      {gap.gap_to_next != null && gap.current_rank > 1 && (
                        <span style={{ fontSize: 10, color: colors.neutral[500] }}>
                          {gap.gap_to_next} to {ordSuffix(gap.current_rank - 1)}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: colors.neutral[400], marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {gap.best_move_summary}
                    </div>
                  </div>
                  {gap.max_gainable_points > 0 && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 3,
                      background: `${colors.success}20`, color: colors.success, flexShrink: 0,
                    }}>
                      +{gap.max_gainable_points}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Contract Liabilities + Summary */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Contract Liabilities */}
          {teamNeeds && (teamNeeds.contract_problems || []).length > 0 && (
            <div>
              <SectionLabel label="CONTRACT LIABILITIES" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {teamNeeds.contract_problems.map((cp, i) => (
                  <div key={i} style={{
                    background: `${colors.error}08`, border: `1px solid ${colors.error}30`,
                    borderRadius: 4, padding: '8px 10px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <PlayerLink mlbId={cp.player_id} name={cp.player_name} style={{ fontSize: 11, fontWeight: 600, color: colors.error }} />
                      <span style={{ fontSize: 10, fontWeight: 600, color: colors.error }}>{cp.liability}</span>
                    </div>
                    <div style={{ fontSize: 10, color: colors.neutral[500], marginTop: 2 }}>
                      ${cp.contract_salary}/yr · {cp.contract_type} · {cp.contract_years}yr
                    </div>
                    <div style={{ fontSize: 10, color: colors.error, fontStyle: 'italic', marginTop: 2 }}>
                      {cp.issue}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary Card */}
          <div style={{
            background: colors.neutral[800], border: `1px solid ${colors.neutral[700]}`,
            borderRadius: 4, padding: '10px 12px',
          }}>
            {teamNeeds && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span style={{ fontSize: 10, color: colors.neutral[500] }}>Overall Rank</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: rankColor(teamNeeds.overall_rank || 99) }}>
                    {ordSuffix(teamNeeds.overall_rank)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span style={{ fontSize: 10, color: colors.neutral[500] }}>Total Points</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: colors.white }}>{teamNeeds.total_points}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span style={{ fontSize: 10, color: colors.neutral[500] }}>Contender Score</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: colors.primary }}>{Math.round((teamNeeds.contender_score || 0) * 100)}%</span>
                </div>
              </>
            )}
            <div style={{
              marginTop: 8, background: `${colors.success}10`, borderRadius: 4, padding: '8px 10px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: colors.success }}>+{gainablePts}</div>
              <div style={{ fontSize: 9, color: colors.neutral[500], marginTop: 2 }}>
                Estimated from {gainableCount} actionable categories via realistic trades & pickups
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default OptimizerGapAnalysis;
